import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { TenantProvider } from "@/hooks/useTenant";
import { DMAFilterProvider } from "@/hooks/useDMAFilter";
import { AuthGuard } from "@/components/AuthGuard";
import { RoleGuard } from "@/components/RoleGuard";

// Eagerly loaded pages (landing + auth)
import Home from "./pages/Home";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";

// Lazy loaded pages
const Dashboard = lazy(() => import("./pages/Index"));
const NewAsset = lazy(() => import("./pages/NewAsset"));
const AssetDetail = lazy(() => import("./pages/AssetDetail"));
const UpdateAsset = lazy(() => import("./pages/UpdateAsset"));
const BoundaryValveAssessment = lazy(() => import("./pages/BoundaryValveAssessment"));
const ZeroPressureTest = lazy(() => import("./pages/ZeroPressureTest"));
const RetestPage = lazy(() => import("./pages/RetestPage"));
const BoundaryValveStatus = lazy(() => import("./pages/BoundaryValveStatus"));
const ValveRepair = lazy(() => import("./pages/ValveRepair"));
const ValveRepairDetail = lazy(() => import("./pages/ValveRepairDetail"));
const ChamberConstruction = lazy(() => import("./pages/ChamberConstruction"));
const ChamberConstructionDetail = lazy(() => import("./pages/ChamberConstructionDetail"));
const ChamberConstructionUpdate = lazy(() => import("./pages/ChamberConstructionUpdate"));
const DMABoundariesMap = lazy(() => import("./pages/DMABoundariesMap"));
const ImportSurveyJSON = lazy(() => import("./pages/ImportSurveyJSON"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const UserManual = lazy(() => import("./pages/UserManual"));
const UserManualAdmin = lazy(() => import("./pages/UserManualAdmin"));
const TenantHomeEditor = lazy(() => import("./pages/TenantHomeEditor"));
const NRWMonitoring = lazy(() => import("./pages/NRWMonitoring"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
            <DMAFilterProvider>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<AuthGuard><RoleGuard allowedRoles={["admin", "surveyor"]} requiredAccess="survey" fallback="/boundary-valves"><Dashboard /></RoleGuard></AuthGuard>} />
              <Route path="/assets/update" element={<AuthGuard><RoleGuard allowedRoles={["admin", "surveyor"]} requiredAccess="survey" fallback="/boundary-valves"><UpdateAsset /></RoleGuard></AuthGuard>} />
              <Route path="/assets/new" element={<AuthGuard><RoleGuard allowedRoles={["admin", "surveyor"]} requiredAccess="survey" fallback="/boundary-valves"><NewAsset /></RoleGuard></AuthGuard>} />
              <Route path="/assets/:id" element={<AuthGuard><RoleGuard allowedRoles={["admin", "surveyor"]} requiredAccess="survey" fallback="/boundary-valves"><AssetDetail /></RoleGuard></AuthGuard>} />
              <Route path="/boundary-valves" element={<AuthGuard><RoleGuard allowedRoles={["admin", "valve_inspector"]} requiredAccess="valve_assessment" fallback="/"><BoundaryValveAssessment /></RoleGuard></AuthGuard>} />
              <Route path="/zero-pressure-test" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="dma_zero_pressure_test" fallback="/"><ZeroPressureTest /></RoleGuard></AuthGuard>} />
              <Route path="/valve-repair" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="valve_repair" fallback="/"><ValveRepair /></RoleGuard></AuthGuard>} />
              <Route path="/boundary-valve-status" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="dma_boundary_valve_status" fallback="/"><BoundaryValveStatus /></RoleGuard></AuthGuard>} />
              <Route path="/valve-repair/detail" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="valve_repair" fallback="/"><ValveRepairDetail /></RoleGuard></AuthGuard>} />
              <Route path="/zero-pressure-test/retest" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="dma_zero_pressure_test" fallback="/"><RetestPage /></RoleGuard></AuthGuard>} />
              
              <Route path="/chamber-construction" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="chamber_construction" fallback="/"><ChamberConstruction /></RoleGuard></AuthGuard>} />
              <Route path="/chamber-construction/detail" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="chamber_construction" fallback="/"><ChamberConstructionDetail /></RoleGuard></AuthGuard>} />
              <Route path="/chamber-construction/update" element={<AuthGuard><RoleGuard allowedRoles={["admin", "engineer", "supervisor", "technician", "valve_inspector"]} requiredAccess="chamber_construction" fallback="/"><ChamberConstructionUpdate /></RoleGuard></AuthGuard>} />
              <Route path="/dma-boundaries" element={<AuthGuard><DMABoundariesMap /></AuthGuard>} />
              <Route path="/import-survey-json" element={<AuthGuard><RoleGuard allowedRoles={["admin"]} requiredAccess="survey" fallback="/"><ImportSurveyJSON /></RoleGuard></AuthGuard>} />
              <Route path="/manual" element={<AuthGuard><UserManual /></AuthGuard>} />
              <Route path="/manual/admin" element={<AuthGuard><UserManualAdmin /></AuthGuard>} />
              <Route path="/tenant-home/edit" element={<AuthGuard><TenantHomeEditor /></AuthGuard>} />
              <Route path="/nrw-monitoring" element={<AuthGuard><NRWMonitoring /></AuthGuard>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </DMAFilterProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;